import {
  EllipsisSpan,
  Table,
  useBreadcrumbRoutes,
  usePaging,
  Wrapper,
  Button,
  Form,
  CustomFilter,
  useUpdate,
  DatePicker,
  Tabs,
  Tag,
  MultiSelect,
} from '@maxtropy/components';
import dayjs, { Dayjs } from 'dayjs';
import { FC, useEffect, useMemo, useState, useCallback } from 'react';
import { ResizeCallbackData } from 'react-resizable';
import ExportModal from './ExportModal';
import styles from './index.module.scss';
import DetailGovernModal from './DetailGovernModal';
import ResizableTitle from './ResizableTitle';
import {
  apiV2EnergyConsumptionAdminPagePost,
  apiV2EnergyConsumptionAdminListPost,
  V2EnergyConsumptionAdminListPostResponse,
  V2EnergyConsumptionAdminPagePostResponse,
  V2EnergyConsumptionPlanGetAreaListPostResponse,
  V2EnergyConsumptionAdminOrgListPostResponse,
  apiV2EnergyConsumptionPlanGetStaffDeptPost,
  apiV2EnergyConsumptionPlanGetAreaList2Post,
} from '@maxtropy/device-customer-apis-v2';
import { formatTimeRange, PlanType, PlanTypeColor } from './util';
import { isEmpty } from 'lodash-es';
const { RangePicker } = DatePicker;

type FilterParams = Omit<any, 'page' | 'size'>;

const getWeekRange = (date: dayjs.Dayjs) => {
  const day = date.day();
  const diffToSat = (day + 1) % 7;
  const start = date.startOf('day').subtract(diffToSat, 'day');
  const end = start.add(6, 'day');
  return [start, end];
};

type TimeDataProps = Exclude<V2EnergyConsumptionAdminPagePostResponse['list'], undefined>[number];

const EnergyGovernance: FC = () => {
  const [form] = Form.useForm();
  const pagingInfo = usePaging(10);
  const [update, setUpdate] = useUpdate();
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [loading, setLoading] = useState<boolean>(false);
  const [sectionOp, setSectionOp] = useState<V2EnergyConsumptionAdminOrgListPostResponse['list']>([]);
  const [areaOp, setAreaOp] = useState<V2EnergyConsumptionPlanGetAreaListPostResponse['list']>([]);
  const [MediaOp, setMediaOp] = useState<V2EnergyConsumptionAdminListPostResponse['list']>([]);
  const [data, setData] = useState<V2EnergyConsumptionAdminPagePostResponse['list']>([]);
  // 每行的 rowSpan 信息，key 为行索引，value 为 rowSpan 数值（0 表示该行被合并隐藏）
  const [rowSpans, setRowSpans] = useState<Record<number, number>>({});
  const [rangeDate, setRangeDate] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [searchParams, setSearchParams] = useState<FilterParams>({});
  const [exportModal, setExportModal] = useState<boolean>(false);
  const [editNodeData, setEditNodeData] = useState<any>(undefined);
  const [tabsId, setTabsId] = useState<string>();
  const [open, setOpen] = useState(false);
  const [detailGovern, setDetailGovern] = useState(false);
  const [buildColumns, setBuildColumns] = useState<any[]>([]);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const orgId = Form.useWatch('ownerOrgId', form);

  const now = dayjs();

  const [currentPeriodStart, currentPeriodEnd] = getWeekRange(now);

  const items = useMemo(() => {
    return (MediaOp || []).map(i => ({ key: String(i.energyMediumId), label: i.energyMediumName }));
  }, [MediaOp]);

  useEffect(() => {
    if (MediaOp && MediaOp.length > 0 && !tabsId) {
      setTabsId(String(MediaOp[0].energyMediumId));
    }
  }, [MediaOp, tabsId]);

  useEffect(() => {
    // 日期初始化
    const defaultStart = currentPeriodStart;
    const defaultEnd = currentPeriodEnd;
    setRangeDate([defaultStart, defaultEnd]);

    apiV2EnergyConsumptionAdminListPost({}).then(res => {
      setMediaOp(res.list);
    });
  }, []);

  useEffect(() => {
    apiV2EnergyConsumptionPlanGetStaffDeptPost({}).then(res => {
      setSectionOp(res.list);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    apiV2EnergyConsumptionAdminPagePost({
      ...searchParams,
      startDate: rangeDate ? rangeDate[0]?.valueOf() : undefined,
      endDate: rangeDate ? rangeDate[1]?.valueOf() : undefined,
      energyMediumId: tabsId,
      page: 1,
      size: 200,
    })
      .then(res => {
        // 先把数据设置到 state
        setData(res.list);

        // 计算同一部门连续行的 rowSpan
        const calcRowSpans = (list: any[]) => {
          const map: Record<number, number> = {};
          for (let i = 0; i < list.length; i++) {
            if (i === 0 || list[i].ownerOrgName !== list[i - 1].ownerOrgName) {
              let span = 1;
              for (let j = i + 1; j < list.length; j++) {
                if (list[j].ownerOrgName === list[i].ownerOrgName) {
                  span++;
                } else {
                  break;
                }
              }
              map[i] = span;
            } else {
              map[i] = 0;
            }
          }
          return map;
        };

        setRowSpans(calcRowSpans(res.list || []));
      })
      .finally(() => setLoading(false));
  }, [searchParams, update, tabsId]);

  useEffect(() => {
    if (!rangeDate) return;
    const [start, end] = rangeDate;

    // 动态生成列；当 rowSpans 更新（来自数据）时也需要重新生成列以读取最新的 rowSpan
    const newColumns = generateColumns(start, end);
    setBuildColumns(newColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, rowSpans, rangeDate]);

  useEffect(() => {
    if (isEmpty(orgId)) return;
    apiV2EnergyConsumptionPlanGetAreaList2Post({ ids: orgId }).then(res => {
      setAreaOp(res.list);
    });
  }, [orgId]);

  const generateColumns = (start: Dayjs, end: Dayjs) => {
    const cols: any[] = [
      {
        title: '部门',
        dataIndex: 'ownerOrgName',
        width: columnWidths['ownerOrgName'] || 120,
        // 使用 render 返回对象来设置 rowSpan，渲染会读取 rowSpans state
        render: (v: string, record: any, rowIndex: number) => {
          const span = rowSpans && typeof rowSpans[rowIndex] !== 'undefined' ? rowSpans[rowIndex] : 1;
          return { children: <EllipsisSpan value={v} />, props: { rowSpan: span } };
        },
      },
      {
        title: '区域',
        width: columnWidths['areaName'] || 160,
        dataIndex: 'areaName',
        render: (v: string) => <EllipsisSpan value={v} />,
      },
      {
        title: '区域供气管道',
        width: columnWidths['gasSupplyPipeline'] || 160,
        dataIndex: 'gasSupplyPipeline',
        render: (v: string) => <EllipsisSpan value={v} />,
      },
    ];

    for (let i = 0; i < 7; i++) {
      const day = start.add(i, 'day');
      const dateStr = day.format('YYYY-MM-DD');
      const dataIndex = `day${i + 1}Data`;
      cols.push({
        title: dateStr,
        dataIndex,
        width: columnWidths[dataIndex] || 160,
        render: (val: TimeDataProps['day1Data']) => {
          const play = (val ?? [])?.filter(v => v.type === 1);
          const delay = (val ?? [])?.filter(v => v.type === 2);
          return (
            <div className={styles.operationalCol}>
              {play.map(p => (
                <Tag border="solid" color={PlanTypeColor[p.type! as PlanType]} key={p.startDateTime}>
                  {formatTimeRange(dateStr, p?.startDateTime, p?.endDateTime)}
                </Tag>
              ))}
              {delay.map(d => (
                <Tag border="solid" color={PlanTypeColor[d.type! as PlanType]} key={d.startDateTime}>
                  {formatTimeRange(dateStr, d?.startDateTime, d?.endDateTime)}
                </Tag>
              ))}
            </div>
          );
        },
        onCell: (record: any, rowIndex: number) => ({
          onClick: () => {
            const ownerOrgId = record.ownerOrgId;
            const areaId = record.areaId;
            const energyMediumId = tabsId;
            setDetailGovern(true);
            setEditNodeData({
              ownerOrgId,
              areaId,
              energyMediumId,
              day: dayjs(dateStr).valueOf(),
            });
          },
          className: styles.operationalCell,
        }),
      });
    }

    return cols;
  };

  const handleResize = useCallback(
    (dataIndex: string) =>
      (_: React.SyntheticEvent, { size }: ResizeCallbackData) => {
        setColumnWidths(prev => ({
          ...prev,
          [dataIndex]: size.width,
        }));
      },
    []
  );

  const mergedColumns = useMemo(
    () =>
      buildColumns.map(col => ({
        ...col,
        width: columnWidths[col.dataIndex] || col.width,
        onHeaderCell: (column: any) => ({
          width: columnWidths[column.dataIndex] || column.width,
          onResize: handleResize(column.dataIndex),
        }),
      })),
    [buildColumns, columnWidths, handleResize]
  );

  const disabledDate = (current: dayjs.Dayjs) => {
    return !current || current < currentPeriodEnd;
  };

  const handleDateSelect = (date: dayjs.Dayjs) => {
    const [start, end] = getWeekRange(date);
    setRangeDate([start, end]);
    setOpen(false);
  };

  const onFinish = (val: FilterParams) => {
    setSearchParams(val);
  };

  const onReset = () => {
    // 日期初始化
    const defaultStart = currentPeriodStart;
    const defaultEnd = currentPeriodEnd;
    setRangeDate([defaultStart, defaultEnd]);
    setAreaOp([]);
    setSearchParams({});
  };

  const operations = (
    <div className={styles.tagExport}>
      <Tag border="solid" color="#52E7FF">
        用能计划
      </Tag>
      <Tag border="solid" color="warning">
        延时用能申请
      </Tag>
      <Button
        type="primary"
        onClick={() => {
          setExportModal(true);
        }}
      >
        导出
      </Button>
    </div>
  );
  const filters = (
    <CustomFilter<FilterParams> form={form} onFinish={val => onFinish(val as FilterParams)} onReset={onReset}>
      <Form.Item name="ownerOrgId" label="部门">
        <MultiSelect
          showSearch
          allowClear
          placeholder="请选择"
          style={{ width: '100%' }}
          optionFilterProp="label"
          options={sectionOp?.map(s => ({ label: s.ownerOrgName, value: s.ownerOrgId }))}
          onChange={() => {
            form.setFieldValue('areaId', undefined);
          }}
        />
      </Form.Item>
      <Form.Item name="areaId" label="区域">
        <MultiSelect
          showSearch
          allowClear
          placeholder="请选择"
          optionFilterProp="label"
          options={areaOp?.map(a => ({ label: a.zoneName, value: a.id }))}
        />
      </Form.Item>
      <Form.Item label="日期范围">
        <RangePicker
          open={open}
          value={rangeDate}
          format="YYYY-MM-DD"
          style={{ width: '100%' }}
          placeholder={['开始日期', '结束日期']}
          // disabledDate={disabledDate}
          onOpenChange={nextOpen => {
            setOpen(nextOpen);
          }}
          onCalendarChange={dates => {
            if (dates && dates[0]) {
              handleDateSelect(dates[0]); // 确保 dates[0] 不为空
            }
          }}
          onChange={() => {
            console.log('我出发了');
          }}
        />
      </Form.Item>
    </CustomFilter>
  );

  return (
    <Wrapper routes={breadcrumbRoutes?.routes} className="page_wrapper" filters={filters}>
      <div className={styles.headItem}>
        <Tabs tabBarExtraContent={operations} items={items} onChange={setTabsId} />
      </div>
      <Table
        rowKey="id"
        sticky
        bordered
        loading={loading}
        dataSource={data}
        className={styles.governTable}
        columns={mergedColumns}
        components={{
          header: {
            cell: ResizableTitle,
          },
        }}
        scroll={{ x: 'max-content' }}
      />
      {exportModal && <ExportModal visible={exportModal} updateFn={setUpdate} onClose={() => setExportModal(false)} />}
      {detailGovern && (
        <DetailGovernModal
          visible={true}
          edit={editNodeData}
          updateFn={setUpdate}
          onClose={() => setDetailGovern(false)}
        />
      )}
    </Wrapper>
  );
};

export default EnergyGovernance;
